import { ref } from 'vue'

// Shared state (initialized once for the entire app)
const avifSupported = ref(null)
const webpSupported = ref(null)
let checkPromise = null

// Helper function to test a specific image format
const testImageFormat = (format, dataUri) => {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = function () {
      resolve(true)
    }
    image.onerror = function () {
      resolve(false)
    }
    image.src = dataUri
  })
}

// Check for format support with multiple test methods for better reliability
const isFormatSupported = (format) => {
  return new Promise((resolve) => {
    if (format === 'avif') {
      // Try multiple AVIF test methods for better compatibility
      Promise.all([
        testImageFormat(
          'avif',
          'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
        ),
        testImageFormat(
          'avif',
          'data:image/avif;base64,AAAAHGZ0eXBhdmlmAAAAAG1pZjFtaWFmAAAA21BTQSAL92kAAAPoAAAAGmluZXJpc28yAAAAMILLbzc1bgAAPFJpc29tYXZpYzAxAQEAAA/IAAIAAAAFAQn//wAAAAAAAAAA'
        ),
      ]).then((results) => {
        // If any test passes, consider it supported
        const isSupported = results.some((result) => result)
        resolve(isSupported)
      })
    } else {
      // WebP test
      testImageFormat(
        'webp',
        'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA'
      ).then(resolve)
    }
  })
}

// Function to check formats once
const checkFormats = () => {
  // If already checking, return existing promise
  if (checkPromise) {
    return checkPromise
  }

  // If values are already determined, return them
  if (avifSupported.value !== null && webpSupported.value !== null) {
    return Promise.resolve({
      avif: avifSupported.value,
      webp: webpSupported.value,
    })
  }

  // Create a promise to check both formats
  checkPromise = Promise.all([isFormatSupported('avif'), isFormatSupported('webp')]).then(
    ([avif, webp]) => {
      avifSupported.value = avif
      webpSupported.value = webp
      return { avif, webp }
    }
  )

  return checkPromise
}

export function useImageFormatSupport() {
  // Initialize the check if not already done
  if (avifSupported.value === null) {
    checkFormats()
  }

  return {
    avifSupported,
    webpSupported,
  }
}
